import { useState } from 'react'
import { RotateCcw, SlidersHorizontal, ChevronDown } from 'lucide-react'
import { getSelectableBranches } from '../../utils/dataHelpers'

const FUEL_TYPES = ['PMS', 'AGO', 'DPK', 'LPG']

const DATE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
]

function filterSummary(filters) {
  const parts = []
  const dateLabel = DATE_OPTIONS.find((o) => o.value === filters.dateFilter)?.label || 'All Time'
  parts.push(
    filters.dateFilter === 'custom' && filters.customRange.start
      ? `${filters.customRange.start} → ${filters.customRange.end}`
      : dateLabel
  )
  if (filters.fuelTypeFilter !== 'all') parts.push(`Fuel: ${filters.fuelTypeFilter}`)
  return parts.join(' • ')
}

function countActiveFilters(filters) {
  return [
    filters.dateFilter !== 'all',
    filters.fuelTypeFilter !== 'all',
    filters.selectedOrg !== 'all',
    filters.selectedBranch !== 'all',
  ].filter(Boolean).length
}

export default function FilterPanel({
  currentUser,
  filters,
  organizations,
  branches,
  onDateFilter,
  onFuelTypeFilter,
  onOrgFilter,
  onBranchFilter,
  onCustomRange,
  onReset,
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  const selectableBranches = getSelectableBranches(currentUser, branches, filters.selectedOrg)
  const isFiltered = countActiveFilters(filters) > 0
  const activeCount = countActiveFilters(filters)

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header row — always visible */}
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        {/* Toggle button — mobile only */}
        <button
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center space-x-2 sm:cursor-default sm:pointer-events-none"
        >
          <SlidersHorizontal className="w-4 h-4 text-gray-500 sm:hidden" />
          <span className="text-sm sm:text-base font-bold text-gray-800">Filters</span>
          {activeCount > 0 && (
            <span className="sm:hidden inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full">
              {activeCount}
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 sm:hidden transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        <div className="flex items-center space-x-2">
          {/* Summary pill — mobile only, when collapsed */}
          {!isExpanded && isFiltered && (
            <span className="sm:hidden text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 font-medium">
              {filterSummary(filters)}
            </span>
          )}
          {isFiltered && (
            <button
              onClick={onReset}
              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 min-h-[36px] px-3 rounded-lg transition"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter controls — hidden on mobile until toggled, always shown on sm+ */}
      <div className={`${isExpanded ? 'block' : 'hidden'} sm:block px-4 pb-4 sm:px-6 sm:pb-5 border-t border-gray-100 sm:border-none pt-3 sm:pt-0`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Date Range */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
            <select
              value={filters.dateFilter}
              onChange={(e) => onDateFilter(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              {DATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Fuel Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fuel Type</label>
            <select
              value={filters.fuelTypeFilter}
              onChange={(e) => onFuelTypeFilter(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="all">All Fuel Types</option>
              {FUEL_TYPES.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          {/* Organization — Super Admin only */}
          {currentUser.role === 'superadmin' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Organization</label>
              <select
                value={filters.selectedOrg}
                onChange={(e) => onOrgFilter(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
              >
                <option value="all">All Organizations</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Branch — Admin + Super Admin */}
          {currentUser.role !== 'manager' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Branch</label>
              <select
                value={filters.selectedBranch}
                onChange={(e) => onBranchFilter(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
              >
                <option value="all">All Branches</option>
                {selectableBranches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Custom date range */}
        {filters.dateFilter === 'custom' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.customRange.start}
                onChange={(e) => onCustomRange({ ...filters.customRange, start: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={filters.customRange.end}
                onChange={(e) => onCustomRange({ ...filters.customRange, end: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        )}

        {/* Summary line — desktop only */}
        <p className="hidden sm:block mt-3 text-xs text-gray-500">
          Showing: <span className="font-semibold text-gray-700">{filterSummary(filters)}</span>
          {isFiltered && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              Filtered
            </span>
          )}
        </p>
      </div>
    </div>
  )
}
